import { Box, Paper, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { formatterService } from "../../NumberFormatter";

import {
  decimal,
  ERROR_KIND_LABEL_KEYS,
  money,
  SERIES_COLORS,
} from "./constants";
import { BacktestCurvePoint } from "./services";

type Side = "baseline" | "ml";
type Unit = "nb_uh" | "revenue" | "adr";
/** Every accumulated error the curve carries: which forecast, absolute or signed, which unit. */
type ErrorField = `${Side}_${"abs" | "err"}_${Unit}`;
/** What the sums divide by: test rows, or the subset of them that carries a price. */
type WeightField = "n" | "adr_n" | "baseline_n" | "baseline_adr_n";

const sum = (points: BacktestCurvePoint[], field: ErrorField | WeightField) =>
  points.reduce((total, point) => total + point[field], 0);

/** Mean over every lead time of the scope. Null values are skipped, not counted as 0. */
const average = (
  points: BacktestCurvePoint[],
  pick: (p: BacktestCurvePoint) => number | null
) => {
  const values = points
    .map(pick)
    .filter((value): value is number => value !== null);
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
};

/**
 * Mean error, row by row, like the breakdown charts below. The API sends the sum of the
 * individual errors, not the error of the sums: a property forecast too high must not cancel
 * one forecast too low.
 */
const meanError = (
  points: BacktestCurvePoint[],
  field: ErrorField,
  weight: WeightField
) => {
  const total = sum(points, weight);
  return total > 0 ? sum(points, field) / total : null;
};

/** How a figure is written out; the locale-aware rendering lives in the component. */
type Style = "nights" | "money" | "price" | "decimal";

const MEASURES = [
  {
    unit: "nb_uh",
    labelKey: "ML_FORECAST_MEASURE_NB_UH",
    weight: "n",
    baselineWeight: "baseline_n",
    format: "nights",
    formatError: "decimal",
  },
  {
    unit: "revenue",
    labelKey: "ML_FORECAST_MEASURE_REVENUE",
    weight: "n",
    baselineWeight: "baseline_n",
    format: "money",
    formatError: "money",
  },
  {
    unit: "adr",
    labelKey: "ML_FORECAST_MEASURE_ADR",
    weight: "adr_n",
    baselineWeight: "baseline_adr_n",
    format: "price",
    formatError: "price",
  },
] as const;

const SERIES_LABEL_KEYS: Record<Side, string> = {
  baseline: "ML_FORECAST_SERIES_BASELINE",
  ml: "ML_FORECAST_SERIES_ML",
};

const KINDS = ["mae", "bias"] as const;

/**
 * What was sold on the filtered scope, and how far each forecast landed from it. Two figures:
 * how far it misses, and which way it leans.
 *
 * The realized total averages over every lead time although it does not vary with it — reading
 * one point of the curve would pin the tiles to whichever comes first.
 */
const MeasureTiles = ({ points }: { points: BacktestCurvePoint[] }) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  if (points.length === 0) return null;

  const write = (style: Style, value: number) => {
    if (style === "price") return money(formatter, value, 2);
    if (style === "money") return money(formatter, value);
    if (style === "decimal") return decimal(formatter, value, 2);
    return decimal(formatter, value);
  };

  const nights = average(points, (point) => point.actual_nb_uh) ?? 0;
  const revenue = average(points, (point) => point.actual_revenue) ?? 0;
  const actual: Record<Unit, number> = {
    nb_uh: nights,
    revenue,
    adr: nights > 0 ? revenue / nights : 0,
  };
  // Its sums are zero where it forecast nothing, so counting rows is the only way to tell
  // "the procedure was right" from "the procedure never ran on this scope".
  const hasBaseline = sum(points, "baseline_n") > 0;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {MEASURES.map((measure) => {
        const weightOf = (side: Side) =>
          side === "ml" ? measure.weight : measure.baselineWeight;
        const figures = (side: Side) =>
          side === "baseline" && !hasBaseline
            ? { mae: null, bias: null }
            : {
                // Each forecast divides by the rows it produced, so a partial baseline is not
                // credited with a zero error on the rows it skipped.
                mae: meanError(
                  points,
                  `${side}_abs_${measure.unit}`,
                  weightOf(side)
                ),
                bias: meanError(
                  points,
                  `${side}_err_${measure.unit}`,
                  weightOf(side)
                ),
              };
        const values: Record<
          Side,
          Record<(typeof KINDS)[number], number | null>
        > = { baseline: figures("baseline"), ml: figures("ml") };

        return (
          <Paper
            key={measure.unit}
            elevation={1}
            sx={{ p: 2, flex: "1 1 340px", minWidth: 320 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                fontWeight: 700,
              }}
            >
              {intl.formatMessage({ id: measure.labelKey })}
            </Typography>

            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {write(measure.format, actual[measure.unit])}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {intl.formatMessage({ id: "ML_FORECAST_TILE_REALIZED" })}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto auto",
                alignItems: "baseline",
                columnGap: 1.5,
                rowGap: 0.75,
                mt: 1.5,
              }}
            >
              <Box />
              <Box />
              {KINDS.map((kind) => (
                <Typography
                  key={kind}
                  variant="caption"
                  sx={{ color: "text.secondary", textAlign: "right" }}
                >
                  {intl.formatMessage({ id: ERROR_KIND_LABEL_KEYS[kind] })}
                </Typography>
              ))}

              {(["baseline", "ml"] as const).map((side) => (
                <Box key={side} sx={{ display: "contents" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: SERIES_COLORS[side],
                    }}
                  />
                  <Typography variant="body2">
                    {intl.formatMessage({ id: SERIES_LABEL_KEYS[side] })}
                  </Typography>

                  {KINDS.map((kind) => {
                    const value = values[side][kind];
                    const reference = values.baseline[kind];
                    // Both columns compare distance to zero: undershooting by 5 is not
                    // better than overshooting by 2.
                    const highlight =
                      side === "ml" &&
                      value !== null &&
                      reference !== null &&
                      Math.abs(value) < Math.abs(reference);
                    // The sign is what the second column is for: a "+" reads as oversold at a
                    // glance where a bare number reads as a distance.
                    const sign = kind === "bias" && value !== null && value > 0;
                    return (
                      <Typography
                        key={kind}
                        variant="body1"
                        sx={{
                          textAlign: "right",
                          color: highlight ? SERIES_COLORS.ml : "text.primary",
                          fontWeight: highlight ? 700 : 400,
                        }}
                      >
                        {value === null
                          ? "—"
                          : `${sign ? "+" : ""}${write(
                              measure.formatError,
                              value
                            )}`}
                      </Typography>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default MeasureTiles;
